import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ClipboardCheck,
  ChevronRight,
  ExternalLink,
  SearchCheck,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DocIconTile } from '../DocChips';
import { ScanImage } from '../ScanImage';
import { BulkRejectBar } from './BulkRejectBar';
import { ScansReviewPanel } from './ScansReviewPanel';
import { SpotCheckReviewPanel } from './SpotCheckReviewPanel';
import { NsReviewPanel } from './NsReviewPanel';
import { ReplacementReviewPanel } from './ReplacementReviewPanel';
import { useDocumentCaptureStore } from '../../store/documentCaptureStore';
import { REVIEW_TAB_ORDER, REVIEW_TAB_LABEL, tabForItem, type ReviewTab } from '../../config/reviewTabs';
import { useDocCaptureEvents } from '../../hooks/useDocCaptureEvents';
import { DocReviewReason, type ReviewQueueItem } from '../../types/documentCapture.types';

/**
 * Review overlay (86d3rrfdk) — the modal that works the exception queue.
 *
 * Left: the queue for the active tab, keyboard-navigable (↑/↓ move, Enter selects, Esc closes via
 * the Dialog). Right: the side-by-side scan image + the plain-language reason banner + the action
 * form for that item's reason. Tabs group by reason (NOT the server's Kind — see reviewTabs.ts).
 */
export function ReviewOverlay() {
  const open = useDocumentCaptureStore((s) => s.reviewOverlayOpen);
  const activeTab = useDocumentCaptureStore((s) => s.reviewActiveTab);
  const items = useDocumentCaptureStore((s) => s.reviewItems);
  const counts = useDocumentCaptureStore((s) => s.reviewCounts);
  const selectedId = useDocumentCaptureStore((s) => s.selectedReviewItemId);
  const closeReview = useDocumentCaptureStore((s) => s.closeReview);
  const setReviewTab = useDocumentCaptureStore((s) => s.setReviewTab);
  const selectReviewItem = useDocumentCaptureStore((s) => s.selectReviewItem);
  const fetchReviewQueue = useDocumentCaptureStore((s) => s.fetchReviewQueue);

  /**
   * 86d3w2jrj FE-3 — keep the QUEUE current while the overlay is open, not just the selected item's
   * document. New work arriving (a decode failing, a replacement being held) and work disappearing
   * (a colleague resolving an item) both change this list, and until now neither did until the
   * overlay was closed and reopened. Only while open: no reason to refetch a list nobody is looking
   * at. The selection effect below already handles the selected item vanishing from under us.
   */
  useDocCaptureEvents({
    enabled: open,
    debounceMs: 250,
    onEvent: () => void fetchReviewQueue(),
  });

  const tabItems = useMemo(
    () => items.filter((item) => tabForItem(item) === activeTab),
    [items, activeTab]
  );
  const selected = tabItems.find((item) => item.id === selectedId) ?? tabItems[0] ?? null;

  // Keep a valid selection: if the current one dropped off the tab (resolved / tab switch), take the first.
  useEffect(() => {
    if (!open) return;
    if (selected && selected.id !== selectedId) selectReviewItem(selected.id);
    if (!selected && selectedId != null) selectReviewItem(null);
  }, [open, selected, selectedId, selectReviewItem]);

  // ↑/↓ move through the queue, Enter selects (focus the panel). Esc is the Dialog's own.
  const listRef = useRef<HTMLUListElement>(null);
  const onListKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (tabItems.length === 0) return;
      const index = tabItems.findIndex((item) => item.id === selected?.id);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectReviewItem(tabItems[Math.min(index + 1, tabItems.length - 1)].id);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectReviewItem(tabItems[Math.max(index - 1, 0)].id);
      }
    },
    [tabItems, selected, selectReviewItem]
  );

  // 86d3x48q7 — bulk selection, SCANS TAB ONLY. The other tabs hold attached documents, where a
  // rejection clears a live NetSuite link; that stays a deliberate one-at-a-time act (and the
  // server refuses attached items in the bulk verb regardless).
  const canSelect = activeTab === 'scans';
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // Drop the selection whenever the visible set changes, so a "Reject 12" can never act on items
  // that scrolled out from under it after a tab switch or a queue refresh.
  useEffect(() => setSelectedIds([]), [activeTab, open]);
  const selectableIds = useMemo(
    () => (canSelect ? tabItems.map((i) => i.id) : []),
    [canSelect, tabItems]
  );
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => selectableIds.includes(id)));
  }, [selectableIds]);
  const allChecked = selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const toggleAll = useCallback(
    () => setSelectedIds(allChecked ? [] : selectableIds),
    [allChecked, selectableIds]
  );
  const toggleOne = useCallback(
    (id: number) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])),
    []
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeReview()}>
      {/*
        The dialog takes the whole viewport less 24px per side. Not greed — this dialog is a
        side-by-side of the SCAN IMAGE and the action form, and the reviewer's job is to read a
        nine-digit number off dithered impact-printer paper on the left. Every pixel not given to
        the scan is a zoom the reviewer has to perform on every single item. The earlier fixed
        1340px left the scan pane around 620px on a 1920 screen and did exactly that.
        The 24px gutter is deliberate: it keeps a strip of backdrop on all four sides so this still
        reads as a dialog over the page rather than a full-screen route. The 1980x1000 caps stop it
        from stretching absurdly on very large monitors — past that width the form column has
        nothing more to do with the space, so the extra just becomes more backdrop.
        Arbitrary values on purpose: viewport-relative dialog dimensions have no spacing-scale
        equivalent.
      */}
      <DialogContent className="flex h-[calc(100vh-3rem)] max-h-[1000px] w-[calc(100vw-3rem)] max-w-[1980px] flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="flex-shrink-0 flex-row items-center gap-2.5 space-y-0 border-b border-border px-4 py-[13px]">
          <ClipboardCheck className="h-[17px] w-[17px] flex-shrink-0 text-primary" aria-hidden />
          <DialogTitle className="text-sm font-bold">Review queue</DialogTitle>
        </DialogHeader>

        {/* tabs */}
        <div className="flex flex-shrink-0 gap-1 border-b border-border px-[14px] py-2">
          {REVIEW_TAB_ORDER.map((tab) => (
            <TabButton key={tab} tab={tab} active={tab === activeTab} count={counts[tab]} onSelect={setReviewTab} />
          ))}
        </div>

        {/*
          Bulk actions span the FULL dialog width, above the columns — not inside the 272px queue.
          The reason is practical, not aesthetic: this block carries a sentence of consequence copy,
          a free-text reason and two buttons. At 272px the textarea clips its own placeholder
          mid-sentence and the confirm/cancel pair wraps, which is exactly how a destructive action
          stops reading as one.
        */}
        {canSelect && <BulkRejectBar selectedIds={selectedIds} onClear={() => setSelectedIds([])} />}

        {/* Queue column pinned at the design's 272px — short ids and one-line reasons need no more,
            and every pixel it takes comes out of the scan. The body fills the dialog's height rather
            than a second viewport-relative guess (70vh inside a fixed-height dialog fought it).
            Below 1200px it drops to 200px, for the same reason the detail grid has a guard: on a
            narrow window the scan pane must never end up narrower than the panes beside it.
            The variant is `md:max-[1200px]:`, never a bare `max-[1200px]:` — a bare max-width rule
            is emitted BEFORE the md: block, so at equal specificity it would lose to md: inside the
            768-1200 band (never firing where it must act) and win below md (killing the stacked
            layout). Scoping it under md: puts it after, and confines it to where it belongs. */}
        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[272px_1fr] md:max-[1200px]:grid-cols-[200px_1fr]">
          {/* queue list */}
          <div className="flex min-h-0 flex-col border-r border-border">
            {canSelect && (
              <div className="flex flex-shrink-0 items-center gap-[7px] border-b border-border bg-muted/40 py-[7px] pl-[11px] pr-2.5">
                <SelectBox
                  checked={allChecked}
                  onChange={toggleAll}
                  label={allChecked ? 'Clear the selection' : 'Select every item in this tab'}
                />
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  {tabItems.length} {tabItems.length === 1 ? 'item' : 'items'}
                </span>
              </div>
            )}
          <ul
            ref={listRef}
            tabIndex={0}
            onKeyDown={onListKeyDown}
            aria-label={`${REVIEW_TAB_LABEL[activeTab]} review items`}
            className="min-h-0 flex-1 divide-y divide-border overflow-y-auto outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {tabItems.map((item) => (
              <QueueRow
                key={item.id}
                item={item}
                selected={item.id === selected?.id}
                onSelect={() => selectReviewItem(item.id)}
                checkable={canSelect}
                checked={selectedIds.includes(item.id)}
                onCheck={() => toggleOne(item.id)}
              />
            ))}
            {tabItems.length === 0 && (
              <li className="p-4 text-sm text-muted-foreground">Nothing in this tab.</li>
            )}
          </ul>
          </div>

          {/* detail */}
          {selected ? (
            <ReviewDetail item={selected} tab={activeTab} />
          ) : (
            <div className="flex items-center justify-center p-6">
              <EmptyState
                icon={ClipboardCheck}
                title="All clear"
                description="Nothing in this tab needs a decision right now."
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabButton({
  tab,
  active,
  count,
  onSelect,
}: {
  tab: ReviewTab;
  active: boolean;
  count: number;
  onSelect: (tab: ReviewTab) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(tab)}
      aria-pressed={active}
      className={cn(
        'h-[30px] rounded-md px-[11px] text-[13px] font-medium transition-colors',
        active ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {/* The count shows even at zero. Hiding it made "NetSuite" and "NetSuite 4" the same width,
          so a tab emptying looked identical to a tab whose number simply wasn't rendered. */}
      {REVIEW_TAB_LABEL[tab]} <span className="tabular-nums">{count}</span>
    </button>
  );
}

/**
 * Selection box. A real button with role=checkbox rather than an <input>, so it can live inside the
 * queue row's own button without nesting interactive elements (invalid HTML, and the inner control
 * stops receiving clicks in Safari).
 */
function SelectBox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="checkbox"
          tabIndex={0}
          aria-checked={checked}
          aria-label={label}
          onClick={(e) => {
            e.stopPropagation();
            onChange();
          }}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              onChange();
            }
          }}
          className={cn(
            'inline-flex h-[15px] w-[15px] flex-shrink-0 cursor-pointer items-center justify-center rounded-[4px] border-[1.5px]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-transparent'
          )}
        >
          {checked && <Check className="h-2.5 w-2.5" aria-hidden />}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function QueueRow({
  item,
  selected,
  onSelect,
  checkable,
  checked,
  onCheck,
}: {
  item: ReviewQueueItem;
  selected: boolean;
  onSelect: () => void;
  checkable: boolean;
  checked: boolean;
  onCheck: () => void;
}) {
  return (
    <li className={cn('flex items-stretch', selected ? 'bg-accent' : 'hover:bg-accent/50')}>
      {checkable && (
        <span className="flex w-8 flex-shrink-0 items-center justify-center">
          <SelectBox checked={checked} onChange={onCheck} label={`Select ${item.docNumber ?? `scan #${item.fileId ?? item.id}`}`} />
        </span>
      )}
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected}
        className="flex min-w-0 flex-1 items-center gap-[9px] py-2.5 pl-0 pr-3 text-left"
      >
        {/* A tile, not a DocTypeChip. "Unclassified" is 90px of text in a 272px column — it pushed
            the scan number, which is what the reviewer is actually looking for, off the row. */}
        <DocIconTile value={item.effectivePrefix} size={25} radius={6} icon={13} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-xs font-semibold text-foreground">
            {item.docNumber ?? `Scan #${item.fileId ?? item.id}`}
          </span>
          {/* WHY it needs a person. Without this the queue is a list of opaque ids and the reviewer
              has to click each one to find out what it wants — the rail beside the page already
              says it, and the queue is where they actually work. */}
          <span className="mt-px block truncate text-[11px] leading-[1.4] text-muted-foreground">
            {item.reasonDetail}
          </span>
        </span>
        {/*
          86d3x48q7 T13 — the spot check is the only item in this queue whose scan is ALREADY LINKED,
          so it needs to be tellable from the ones still awaiting a number BEFORE it is opened.
          Without it a reviewer working top-down hits an item that looks unresolved and finds a
          panel with nothing to type. Icon-only at this width — the labelled chip clipped mid-word
          against the 272px column, which is worse than no marker at all.
        */}
        {item.reason === DocReviewReason.AutoLinkAudit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label="Spot check — already linked, confirm it went to the right document"
                className="flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-[5px] bg-info/12 text-info"
              >
                <SearchCheck className="h-3 w-3" aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent>Spot check — already linked, confirm it went to the right document</TooltipContent>
          </Tooltip>
        )}
        {selected && <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />}
      </button>
    </li>
  );
}

/** Right pane: scan image + reason banner + the reason-appropriate action form. */
/** Zoom stops for the scan pane (design bundle). Fit is 1; below it shrinks, above it magnifies. */
const ZOOMS = [0.8, 1, 1.25, 1.5, 2, 2.5, 3];

function stepZoom(current: number, direction: 1 | -1): number {
  const at = ZOOMS.indexOf(current);
  const next = (at < 0 ? ZOOMS.indexOf(1) : at) + direction;
  return ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, next))];
}

function ZoomButton({
  label,
  icon: Icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: typeof ZoomIn;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Icon-only control: tooltip AND aria-label, per the UI gate. */}
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md border border-input bg-card text-foreground hover:bg-accent disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon className="h-[13px] w-[13px]" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * 86d3z2nz4 — the route from a review item to its DOCUMENT.
 *
 * This overlay had no link to the viewer at all. That was survivable while a document-level review row
 * in the live stream still carried one; now that every review row opens this overlay instead, its
 * absence would have made the change a net LOSS for exactly those items (NetSuite ambiguity, voided,
 * link failed) — the reviewer would arrive here with no way to look at the filed document.
 *
 * Rendered only for an item with a real document behind it. A scan-level item shows nothing: its
 * number is a SUGGESTION and `/d/{number}` would land on "Document not found", which is the very
 * production bug (2026-08-05, DR-000044843) that made those rows inert in the first place. `documentId`
 * is the honest test — `docNumber` alone is not, and that distinction is the whole point.
 *
 * A new tab, deliberately: navigating in place would unmount the page that owns this dialog and drop
 * the reviewer out of a decision they are half-way through. The visible text leads the accessible
 * name, so the two never disagree (WCAG 2.5.3).
 */
function OpenDocumentLink({ item }: { item: ReviewQueueItem }) {
  if (item.documentId == null || !item.docNumber) return null;
  return (
    <Link
      to={`/d/${item.docNumber}`}
      target="_blank"
      rel="noopener"
      aria-label={`Open document ${item.docNumber} in a new tab`}
      className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      Open document
      <ExternalLink className="h-3 w-3" aria-hidden />
    </Link>
  );
}

function ReviewDetail({ item, tab }: { item: ReviewQueueItem; tab: ReviewTab }) {
  const [zoom, setZoom] = useState(1);
  // Reset to fit when the reviewer moves to another item — carrying 3x across a selection change
  // would drop them into a corner of a scan they have not seen yet.
  useEffect(() => setZoom(1), [item.id]);

  // The form column is a CLAMP, not a fraction, and that distinction is the whole point. The two
  // panes are not doing equal work: the left is paper a person has to read a nine-digit number off,
  // the right is a field, a check list and two buttons. That form needs about 400px and never more
  // — hand it a fraction and it grows with the window, spending pixels where nothing needs them.
  // Clamped at 360-460px, every pixel the dialog gains above that goes to the scan instead. (A
  // ratio was tried: 1.45fr read right inside the old 1340px dialog and wrong inside 1900.)
  // Below 1200px the clamp's 360px floor would start crowding the scan, so the guard pins the form
  // at 300px: the scan pane must never end up narrower than the form beside it. Same variant
  // discipline as the queue grid — `lg:max-[1200px]:`, never a bare `max-[1200px]:`, which would be
  // emitted before the lg: block and so lose inside the band and win below it, inverting the very
  // layout it exists to protect. Under lg the panes stack and the scan is full-width anyway.
  return (
    <div className="grid min-h-0 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_clamp(360px,30%,460px)] lg:max-[1200px]:grid-cols-[minmax(0,1fr)_300px]">
      {/*
        Scan pane with a zoom control. The reviewer's entire job here is reading a nine-digit number
        off scanned paper and comparing it against a candidate — and these are dithered impact-printer
        scans, which is the whole reason this feature exists. Fit-to-pane is often not enough to tell
        …626 from …628, which is exactly the one-character difference being hunted.
      */}
      <div className="flex min-h-0 flex-col border-b border-border bg-muted/[0.35] lg:border-b-0 lg:border-r">
        <div className="flex flex-shrink-0 items-center gap-1.5 border-b border-border bg-card px-3 py-[7px]">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">Scan</span>
          <span className="flex-1" />
          <ZoomButton label="Zoom out" icon={ZoomOut} onClick={() => setZoom((z) => stepZoom(z, -1))} disabled={zoom <= ZOOMS[0]} />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="h-[26px] min-w-[52px] rounded-md border border-input bg-card text-[11px] font-semibold tabular-nums text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {Math.round(zoom * 100)}%
              </button>
            </TooltipTrigger>
            <TooltipContent>Reset to fit</TooltipContent>
          </Tooltip>
          <ZoomButton
            label="Zoom in — magnify the scan to cross-check details"
            icon={ZoomIn}
            onClick={() => setZoom((z) => stepZoom(z, 1))}
            disabled={zoom >= ZOOMS[ZOOMS.length - 1]}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-[14px]">
          {/* Width, not `zoom`: `zoom` is non-standard and doesn't reflow the scroll container,
              so the magnified image would clip instead of becoming pannable. */}
          <div style={{ width: `${zoom * 100}%` }} className="origin-top-left">
            {/* The scan sits on a raised white sheet against the tinted pane — paper on a desk.
                Edge-to-edge it merged into the pane and the page boundary disappeared.
                `bg-white` is a deliberate UI-gate exception, not an oversight: the mount holds a
                scanned page whose own pixels are white in every theme, so a themed `card` surface
                would ring the image with a visible halo in dark and Industrial. */}
            <div className="min-h-[330px] rounded-[10px] border border-border bg-white p-5 shadow-sm">
              {/*
                ALWAYS by review item — never by `docNumber`, even when the item has one.

                The by-docNumber endpoint resolves the document's ACTIVE version, and a review item
                is frequently about a different file: a replacement sits at version 2 while version 1
                is still active, and a spot check names a document whose newer version is pending. So
                the docNumber path showed the reviewer the WRONG PIECE OF PAPER and asked them to
                confirm against it — and where the active version had no rendered page it 404'd into
                "Scan image unavailable" instead, which is how this was noticed.

                `review/{id}/pages/{n}` resolves the item's own `FileId`, which is the scan the
                decision is actually about, and applies the same visibility gate as the queue.
              */}
              <ScanImage
                reviewItemId={item.id}
                page={1}
                alt={item.docNumber ? `Scan under review for ${item.docNumber}` : 'Scan awaiting a number'}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* reason + action form */}
      <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4">
        <div className="flex items-center gap-2">
          <DocIconTile value={item.effectivePrefix} size={25} radius={6} icon={13} />
          <span className="font-mono text-[13px] font-semibold text-foreground">
            {item.docNumber ?? `Scan #${item.fileId ?? item.id}`}
          </span>
          <OpenDocumentLink item={item} />
        </div>
        {/* verbatim server sentence */}
        <p className="rounded-[10px] bg-muted px-3 py-[11px] text-[12.5px] leading-[1.55] text-foreground">
          {item.reasonDetail}
        </p>

        {/*
          key={item.id} — belt to the panels' own braces (86d3w2jrj FE-2). Selecting a different item
          remounts rather than re-renders, so no panel can carry the previous item's typed number,
          open reject box, or in-flight fetch across the switch. The panels are individually fenced
          too; this makes the guarantee structural rather than something each panel must remember.
        */}
        {/*
          86d3x48q7 T13 — the spot check is the one Scans item that is ALREADY LINKED, so it asks a
          different question ("is the document it went to right?") and offers a different pair of
          answers (confirm / unlink). Routing it through the assign form would show a number field
          for a scan that needs no number, and make "confirm" look like a re-link when it changes
          nothing. Branching here keeps ScansReviewPanel about identifying an unidentified scan.
        */}
        {tab === 'scans' &&
          (item.reason === DocReviewReason.AutoLinkAudit ? (
            <SpotCheckReviewPanel key={item.id} item={item} />
          ) : (
            <ScansReviewPanel key={item.id} item={item} />
          ))}
        {tab === 'netsuite' && <NsReviewPanel key={item.id} item={item} />}
        {tab === 'replacements' && <ReplacementReviewPanel key={item.id} item={item} />}

        {/* The overlay has always been arrow/enter/esc navigable; nothing said so, so nobody used it.
            `mt-auto` pins it to the bottom of the pane instead of letting it float directly under
            whichever panel happens to be shortest. */}
        <p className="mt-auto border-t border-border pt-[11px] text-[10.5px] text-muted-foreground">
          ↑↓ move · Enter select · Esc close
        </p>
      </div>
    </div>
  );
}
